/**
 * SummaryCharts — at-a-glance visual strip for the Summary tab.
 *
 * Three charts sourced from data we already have in the session —
 * no extra backend calls:
 *
 *  1. Severity donut: share of tables by critical/warning/good/excellent
 *  2. Largest tables bar: top-10 tables by row count
 *  3. Quality dimensions radar: aggregate completeness / validity /
 *     uniqueness / consistency / accuracy across all tables (DAMA-5)
 */

import { useState } from "react";
import { HelpCircle } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "../../ThemeContext";

interface TableLike {
  table_name: string;
  row_count: number;
  severity?: string;
  score?: number;
}

interface SummaryChartsProps {
  tables: TableLike[];
  dimensionScores?: Record<string, number>;
}

// Read the tier colours from the same --readiness-* CSS variables the Tables
// tab (FilterBar chips, TableCard badges/accent) uses — so the donut can't
// diverge from them, and it stays theme-aware automatically (the previous
// hardcoded hex was dark-mode-only and disagreed with the Tables tab, e.g.
// "good" rendered blue in the donut but green on the cards). Recharts `fill`
// accepts a `var(...)` string.
const SEVERITY_COLORS: Record<string, string> = {
  excellent: "var(--readiness-excellent)",
  good: "var(--readiness-good)",
  moderate: "var(--readiness-moderate)",
  poor: "var(--readiness-poor)",
  warning: "var(--readiness-warning)",
  critical: "var(--readiness-critical)",
};

function compactNum(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
}

export function SummaryCharts({ tables, dimensionScores }: SummaryChartsProps) {
  const [showDimLegend, setShowDimLegend] = useState(false);
  // Recharts renders axis/grid/tooltip colors as inline SVG attributes, not
  // CSS classes — the .light overrides in ztd-design-system.css can't reach
  // them, so these need an explicit light/dark switch here instead.
  const { isDark } = useTheme();
  const chartGridStroke = isDark
    ? "rgba(255,255,255,0.05)"
    : "rgba(0,0,0,0.08)";
  const chartAxisTick = isDark ? "#94a3b8" : "#475569";
  const chartCategoryTick = isDark ? "#cbd5e1" : "#334155";
  const chartTooltipStyle = {
    background: isDark ? "rgba(15,23,42,0.95)" : "rgba(255,255,255,0.98)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.12)"}`,
    borderRadius: 6,
    fontSize: 12,
    color: isDark ? undefined : "#1a1a2e",
  };
  const chartRadarColor = isDark ? "#34d399" : "#059669";
  // ── Severity distribution donut ───────────────────────────────────
  const severityBuckets: Record<string, number> = {
    excellent: 0,
    good: 0,
    moderate: 0,
    poor: 0,
    critical: 0,
  };
  for (const t of tables) {
    const sev = (t.severity || "good").toLowerCase();
    // Map legacy "warning" → "moderate" for backward-compat with old sessions.
    const normalized = sev === "warning" ? "moderate" : sev;
    if (normalized in severityBuckets) severityBuckets[normalized]++;
    else severityBuckets.moderate++;
  }
  const severityData = Object.entries(severityBuckets)
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value }));

  // ── Top-10 largest tables bar ─────────────────────────────────────
  const topTables = [...tables]
    .sort((a, b) => (b.row_count || 0) - (a.row_count || 0))
    .slice(0, 10)
    .map((t) => ({
      name:
        t.table_name.length > 18
          ? t.table_name.slice(0, 17) + "…"
          : t.table_name,
      fullName: t.table_name,
      rows: t.row_count || 0,
      fill: SEVERITY_COLORS[(t.severity || "good").toLowerCase()] || "#60a5fa",
    }));

  // ── Quality dimensions radar ──────────────────────────────────────
  //
  // April 2026 fix (user-reported "Completeness 0%"): the prior aggregate
  // was a severity-penalty rollup that capped at 0 for any moderately
  // noisy category — duplicate business-rule findings (since cleaned)
  // were tipping completeness past the cap and the radar showed 0%.
  //
  // New behaviour:
  //   1. If any table carries per-table ``quality_dimensions`` from the
  //      profiler, average them across non-empty tables (row_count > 0).
  //      That gives a meaningful 0-100 readout regardless of finding noise.
  //   2. Otherwise fall back to the explicit ``dimensionScores`` prop
  //      (backend-provided severity rollup), used only when no per-table
  //      data is available.
  const tablesWithDims = tables.filter(
    (t) =>
      t.row_count &&
      t.row_count > 0 &&
      // @ts-expect-error mypy cleanup — runtime contract is correct, type model needs follow-up
      t.quality_dimensions &&
      // @ts-expect-error mypy cleanup — runtime contract is correct, type model needs follow-up
      Object.keys(t.quality_dimensions).length > 0,
  );
  let dims: Record<string, number> | null = null;
  if (tablesWithDims.length > 0) {
    const sums: Record<string, number> = {};
    const counts: Record<string, number> = {};
    for (const t of tablesWithDims) {
      // @ts-expect-error mypy cleanup — runtime contract is correct, type model needs follow-up
      for (const [k, v] of Object.entries(t.quality_dimensions || {})) {
        if (typeof v !== "number" || Number.isNaN(v)) continue;
        sums[k] = (sums[k] ?? 0) + v;
        counts[k] = (counts[k] ?? 0) + 1;
      }
    }
    const avg: Record<string, number> = {};
    for (const k of Object.keys(sums)) {
      avg[k] = sums[k] / Math.max(1, counts[k]);
    }
    if (Object.keys(avg).length > 0) {
      dims = avg;
    }
  }
  if (!dims && dimensionScores && Object.keys(dimensionScores).length > 0) {
    dims = dimensionScores;
  }
  const radarData = dims
    ? Object.entries(dims).map(([dim, value]) => ({
        dimension: dim.charAt(0).toUpperCase() + dim.slice(1),
        value: Math.round(value),
      }))
    : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Quality score distribution — per-table grade tier (excellent/good/
          moderate/poor/critical), NOT to be confused with IssueSeverity
          (critical/high/medium/low/info), which rates individual findings. */}
      <div className="glass-surface rounded-xl p-4">
        <h4 className="text-sm font-semibold text-white/85 mb-2">
          Quality Score Distribution
        </h4>
        <p className="text-[11px] text-white/55 mb-3">
          How many tables land in each quality tier.
        </p>
        {severityData.length === 0 ? (
          <p className="text-xs text-white/50">No data.</p>
        ) : (
          <div style={{ height: 220 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={severityData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={2}
                >
                  {severityData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={SEVERITY_COLORS[entry.name] || "#60a5fa"}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v: any, n: any) => [`${v} tables`, n]}
                  contentStyle={chartTooltipStyle}
                />
                <Legend
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Largest tables */}
      <div className="glass-surface rounded-xl p-4">
        <h4 className="text-sm font-semibold text-white/85 mb-2">
          Largest Tables by Row Count
        </h4>
        <p className="text-[11px] text-white/55 mb-3">
          Where the volume — and therefore the DQ risk — concentrates.
        </p>
        {topTables.length === 0 ? (
          <p className="text-xs text-white/50">No data.</p>
        ) : (
          <div style={{ height: Math.max(260, topTables.length * 28) }}>
            <ResponsiveContainer>
              <BarChart
                data={topTables}
                layout="vertical"
                margin={{ top: 4, right: 12, bottom: 4, left: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                <XAxis
                  type="number"
                  tick={{ fill: chartAxisTick, fontSize: 10 }}
                  tickFormatter={compactNum}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  // Wider axis so longer table names fit; `interval={0}` forces
                  // Recharts to render every tick (it was auto-hiding
                  // alternate labels at compact heights, which left the Y
                  // axis looking half-blank).
                  width={140}
                  interval={0}
                  tick={{ fill: chartCategoryTick, fontSize: 10 }}
                />
                <Tooltip
                  formatter={(v: any) => [
                    `${Number(v).toLocaleString()} rows`,
                    "Row count",
                  ]}
                  labelFormatter={(_l, payload: any) =>
                    payload?.[0]?.payload?.fullName || ""
                  }
                  contentStyle={chartTooltipStyle}
                />
                <Bar dataKey="rows">
                  {topTables.map((entry) => (
                    <Cell key={entry.fullName} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Quality dimensions radar */}
      <div className="glass-surface rounded-xl p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h4 className="text-sm font-semibold text-white/85">
            Quality Dimensions
          </h4>
          {/* Explicit, clickable help toggle — native `title` tooltips
              were too easy to miss. Clicking expands a legend panel
              below the numeric readout with a plain-English definition
              for each dimension. */}
          <button
            onClick={() => setShowDimLegend((s) => !s)}
            className={`shrink-0 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border transition-colors ${
              showDimLegend
                ? "border-[var(--primary)]/40 bg-[var(--primary)]/15 text-[var(--primary)]"
                : "border-white/10 text-white/55 hover:text-white/85 hover:bg-white/5"
            }`}
            aria-expanded={showDimLegend}
            aria-controls="dim-legend"
            title="Show / hide the dimension glossary"
          >
            <HelpCircle className="w-3 h-3" />
            {showDimLegend ? "Hide legend" : "What do these mean?"}
          </button>
        </div>
        <p className="text-[11px] text-white/55 mb-3">
          Aggregate completeness / validity / uniqueness / consistency /
          accuracy — one shape tells you where the weak spots are.
        </p>
        {radarData.length === 0 ? (
          <p className="text-xs text-white/50">No dimension scores yet.</p>
        ) : (
          <>
            <div style={{ height: 180 }}>
              <ResponsiveContainer>
                <RadarChart data={radarData} outerRadius={70}>
                  <PolarGrid stroke={chartGridStroke} />
                  <PolarAngleAxis
                    dataKey="dimension"
                    tick={{ fill: chartCategoryTick, fontSize: 11 }}
                  />
                  <Radar
                    dataKey="value"
                    stroke={chartRadarColor}
                    fill={chartRadarColor}
                    fillOpacity={0.35}
                  />
                  <Tooltip
                    formatter={(v: any) => `${v}%`}
                    contentStyle={chartTooltipStyle}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            {/* Numeric readout — each row has a visible HelpCircle icon
                that shows the dimension's definition on hover. The
                button toggle above opens a fully inline legend for
                users who prefer a persistent reference. */}
            <ul className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
              {radarData.map((d) => (
                <li
                  key={d.dimension}
                  className="flex items-center justify-between"
                >
                  <span className="text-white/70 inline-flex items-center gap-1">
                    {d.dimension}
                    <HelpCircle
                      className="w-3 h-3 text-white/35 hover:text-white/70 cursor-help"
                      aria-label={`${d.dimension} definition`}
                      // @ts-expect-error mypy cleanup — runtime contract is correct, type model needs follow-up
                      title={DIMENSION_GLOSSARY[d.dimension] ?? ""}
                    />
                  </span>
                  <span
                    className={`font-mono ${
                      d.value >= 90
                        ? "text-emerald-300"
                        : d.value >= 70
                          ? "text-lime-300"
                          : d.value >= 50
                            ? "text-amber-300"
                            : "text-red-300"
                    }`}
                  >
                    {d.value}%
                  </span>
                </li>
              ))}
            </ul>
            {showDimLegend && (
              <dl
                id="dim-legend"
                className="mt-3 pt-3 border-t border-white/10 space-y-2 text-[11px]"
              >
                {radarData.map((d) => (
                  <div
                    key={d.dimension}
                    className="grid grid-cols-[80px_1fr] gap-2"
                  >
                    <dt className="text-white/80 font-medium">{d.dimension}</dt>
                    <dd className="text-white/55 leading-snug">
                      {DIMENSION_GLOSSARY[d.dimension] ??
                        "No definition available."}
                    </dd>
                  </div>
                ))}
                <p className="pt-1 text-[11px] text-white/35 italic">
                  Scoring scale: 90–100 excellent · 75–89 good · 60–74 moderate
                  · 40–59 poor · below 40 critical.
                </p>
              </dl>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Plain-English glossary of each quality dimension — attached as a tooltip
 * on the numeric readout so users can learn what each percentage means
 * without leaving the page.
 */
// DAMA-aligned quality dimensions. Backend `compute_quality_dimensions`
// always emits all five keys per table; the radar iterates whatever
// the backend provides, so this glossary just needs an entry per
// dimension we expect to render.
const DIMENSION_GLOSSARY: Record<string, string> = {
  Completeness:
    "Share of required fields that actually have a value. 100% = no nulls in tracked columns; 0% = critical null patterns (e.g. a required column is always empty).",
  Validity:
    "Share of values that match their declared type / format / domain — dates parse, numerics within bounds, no sentinel values like NOT_MAPPED. 0% = systemic type or format failures.",
  Uniqueness:
    "Share of expected-unique columns with no duplicates. Primary-key candidates use this; also penalised by fuzzy near-duplicates. 100% = clean PK; 0% = many duplicates.",
  Consistency:
    "Cross-field and cross-row agreement — format variations, legacy-vs-new code coexistence, trailing whitespace, cross-column dependency mismatches. 100% = consistent encoding throughout.",
  Accuracy:
    "Degree to which the data correctly describes reality — orphan foreign keys, date-order violations (start > end), and other referential / correctness issues. 100% = referential integrity intact.",
};
